/**
 * TileShell — Common card chrome for every tile.
 *   - Title + icon + subtitle (type · source · timeframe)
 *   - Kebab menu (Edit / Duplicate / Delete / View Config) — fires callbacks up to BoardDetail
 *   - Skeleton + error states
 *   - cohort_scope chip when set
 */
import React, { useState } from 'react';
import {
  Card, CardBody, CardHeader, Flex, HStack, Text, Box, Tooltip, Badge,
  IconButton, Menu, MenuButton, MenuList, MenuItem, MenuDivider, Portal,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter, Button, useDisclosure, useToast,
} from '@chakra-ui/react';
import TileSkeleton from './TileSkeleton';
import {
  FiZap, FiTrendingUp, FiActivity, FiBarChart2, FiList, FiTarget,
  FiUsers, FiPhone, FiPieChart, FiMoreVertical, FiEdit2, FiCopy, FiTrash2, FiCode,
} from 'react-icons/fi';

const ICONS = {
  big_number: FiZap, time_series: FiTrendingUp, funnel: FiActivity, breakdown: FiBarChart2,
  kpi_table: FiList, segment_leaderboard: FiTarget, profile_breakdown: FiPieChart,
  channel_reachability: FiPhone, retention: FiTrendingUp, distribution: FiBarChart2, path: FiActivity,
};

// Business-friendly labels for the View-config modal — these mirror the same
// labels the TileEditor uses, kept local to avoid a cross-import.
const niceSourceTS = (s) => ({ events: 'Events', contacts: 'Contacts', cohort_membership: 'Cohort Membership' }[s] || s || '—');
const niceTypeTS   = (t) => ({
  big_number: 'Big Number', time_series: 'Area Chart', funnel: 'Funnel', breakdown: 'Breakdown',
  kpi_table: 'KPI Table', segment_leaderboard: 'Segment Leaderboard', profile_breakdown: 'Profile Breakdown',
  channel_reachability: 'Channel Reachability', retention: 'Retention', distribution: 'Distribution',
  flow: 'User Flow', path: 'Path / Sankey',
}[t] || t || '—');

// Human-readable summary of a cohort's first 1-2 conditions.
// Cohort condition shape: { type:'event', event, property, operator, value, did_not_perform, time_frame }.
function summarizeCohortConditions(cohort) {
  if (!cohort) return null;
  const conds = Array.isArray(cohort.conditions) ? cohort.conditions.slice(0, 2) : [];
  if (conds.length === 0) return null;
  const opPretty = { equals: '=', not_equals: '≠', greater_than: '>', less_than: '<',
    greater_than_or_equal: '≥', less_than_or_equal: '≤', contains: 'contains',
    in_list: 'in', not_in_list: 'not in', contains_any: 'contains any' };
  const fmt = (c) => {
    if (c.did_not_perform) return `did NOT perform ${c.event}`;
    if (c.property === 'count') return `${c.event} ${opPretty[c.operator] || c.operator} ${c.value}× (${(c.time_frame || 'all_time').replace(/_/g, ' ')})`;
    if (c.property) return `${c.event}.${c.property} ${opPretty[c.operator] || c.operator} ${Array.isArray(c.value) ? `[${c.value.join(', ')}]` : c.value}`;
    return `performed ${c.event}`;
  };
  const more = (cohort.conditions?.length || 0) > 2 ? ` +${cohort.conditions.length - 2} more` : '';
  const match = cohort.match === 'any' ? ' OR ' : ' AND ';
  return conds.map(fmt).join(match) + more;
}

// Bug-016 — surface WHICH event(s) an event-sourced tile represents, so a
// card titled e.g. "Signups" still shows it's built on `user_signed_up`.
// Mirrors the various viz_config shapes the TileEditor writes per tile type.
function eventSummary(tile) {
  if (!tile || tile.source !== 'events') return null;
  const vc = tile.viz_config || {};
  const joinEvents = (arr) => {
    const list = arr.filter(Boolean);
    if (list.length === 0) return null;
    if (list.length > 2) return `${list.length} events`;
    return list.join(', ');
  };

  // Funnel — ordered steps.
  if (Array.isArray(vc.steps) && vc.steps.length) {
    const names = vc.steps.map((s) => (typeof s === 'string' ? s : s?.event)).filter(Boolean);
    return joinEvents(names);
  }
  // Flow — anchored on one event.
  if (vc.anchor_event) return vc.anchor_event;
  // Breakdown sweep — source → sink.
  if (vc.source_event || vc.sink_event) {
    return [vc.source_event, vc.sink_event].filter(Boolean).join(' → ');
  }
  // Multi-event tiles (breakdown attribution, etc.).
  if (Array.isArray(vc.events) && vc.events.length) return joinEvents(vc.events);
  // Single-event tiles (big number, time series, distribution…).
  if (vc.event) return String(vc.event);
  return null;
}

export default function TileShell({ tile, loading, error, children, editMode, onEdit, onDuplicate, onDelete, cohort }) {
  const Icon = ICONS[tile?.type] || FiUsers;
  const toast = useToast();
  const configModal = useDisclosure();
  const deleteAlert = useDisclosure();
  const cancelRef = React.useRef();
  const [busy, setBusy] = useState(false);

  // Subtitle = ONLY business-meaningful context (time range).
  // Tile type + source are internal metadata — never shown to the user.
  const TIMEFRAME_LABEL = {
    last_24_hours: 'Last 24 hours',
    last_7_days:   'Last 7 days',
    last_30_days:  'Last 30 days',
    last_90_days:  'Last 90 days',
    last_year:     'Last year',
    all_time:      'All time',
    custom_range:  'Custom range',
  };
  const timeframe = tile?.viz_config?.time_frame;
  const subtitleText = timeframe ? (TIMEFRAME_LABEL[timeframe] || timeframe.replace(/_/g, ' ')) : null;

  const cohortSummary = summarizeCohortConditions(cohort);
  const eventText = eventSummary(tile);

  const handleDuplicate = async () => {
    if (!onDuplicate) return;
    setBusy(true);
    try { await onDuplicate(tile); toast({ status: 'success', title: 'Duplicated', duration: 2000 }); }
    catch (e) { toast({ status: 'error', title: 'Duplicate failed', description: e.message }); }
    finally { setBusy(false); }
  };
  const handleDelete = async () => {
    if (!onDelete) return;
    setBusy(true);
    try { await onDelete(tile); deleteAlert.onClose(); toast({ status: 'success', title: 'Tile deleted', duration: 2000 }); }
    catch (e) { toast({ status: 'error', title: 'Delete failed', description: e.message }); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Card chrome — matches portal BoxWrapper convention:
          1px gray border + white bg + 15px radius. Subtle shadow for tile lift. */}
      <Card border="1px solid #0000001a" bg="white" borderRadius="15px" overflow="hidden" h="100%" boxShadow="sm">
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={2}>
            <HStack spacing={2} flex={1} minW={0}>
              <Box color="var(--brand-primary)" flexShrink={0}><Icon size={16} /></Box>
              <Box minW={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>{tile?.title || 'Untitled tile'}</Text>
                {/* Bug-016 — which event(s) this event-sourced card represents */}
                {eventText && (
                  <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1} fontFamily="mono">{eventText}</Text>
                )}
                {subtitleText && (
                  <Text fontSize="xs" color="gray.500" mt={0.5}>{subtitleText}</Text>
                )}
                {/* Cohort definition — answers "who exactly is in this cohort?" */}
                {tile?.cohort_scope && cohort && (
                  <Text fontSize="xs" color="purple.600" mt={1} noOfLines={2} fontStyle="italic">
                    🎯 {cohort.name}{cohortSummary && <> = <Text as="span" fontFamily="mono" color="purple.700">{cohortSummary}</Text></>}
                  </Text>
                )}
              </Box>
            </HStack>
            <HStack spacing={1} flexShrink={0}>
              {tile?.cohort_scope && (
                <Tooltip placement="bottom-end" hasArrow label={
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold">Cohort: {cohort?.name || 'unknown'}</Text>
                    {cohortSummary && <Text fontSize="xs" color="gray.200" mt={1}>= {cohortSummary}</Text>}
                    {cohort?.user_count != null && (
                      <Text fontSize="xs" color="gray.300" mt={1}>{Number(cohort.user_count).toLocaleString()} members</Text>
                    )}
                  </Box>
                }>
                  <Badge bg="#7c3aed" color="white" fontSize="2xs" cursor="help" borderRadius="full">scoped</Badge>
                </Tooltip>
              )}
              {editMode && (onEdit || onDuplicate || onDelete) && (
                <Menu placement="bottom-end" isLazy strategy="fixed">
                  <MenuButton as={IconButton} icon={<FiMoreVertical />} size="xs" variant="ghost" aria-label="Tile menu" />
                  <Portal>
                    <MenuList fontSize="sm" minW="180px" zIndex={9999}>
                      {onEdit && <MenuItem icon={<FiEdit2 />} onClick={() => onEdit(tile)}>Edit</MenuItem>}
                      {onDuplicate && <MenuItem icon={<FiCopy />} onClick={handleDuplicate} isDisabled={busy}>Duplicate</MenuItem>}
                      <MenuItem icon={<FiCode />} onClick={configModal.onOpen}>View config</MenuItem>
                      {onDelete && (
                        <>
                          <MenuDivider />
                          <MenuItem icon={<FiTrash2 />} color="red.600" onClick={deleteAlert.onOpen}>Delete</MenuItem>
                        </>
                      )}
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={2}>
          {loading && <TileSkeleton tile={tile} />}
          {!loading && error && (
            <Box p={3} bg="orange.50" borderRadius="md" borderLeft="3px solid" borderColor="orange.400">
              <Text fontSize="xs" fontWeight="medium" color="orange.700">⚠ Unable to render</Text>
              <Text fontSize="xs" color="orange.600" mt={1}>{error.message || error.code || 'Unknown error'}</Text>
            </Box>
          )}
          {!loading && !error && children}
        </CardBody>
      </Card>

      {/* View config modal — show humanized labels alongside the raw JSON. */}
      <Modal isOpen={configModal.isOpen} onClose={configModal.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Tile config — {tile?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box mb={3}>
              <Text fontSize="xs" color="gray.500">Data source</Text>
              <Text fontSize="sm" fontWeight="medium">{niceSourceTS(tile?.source)}</Text>
              <Text fontSize="xs" color="gray.500" mt={2}>Card type</Text>
              <Text fontSize="sm" fontWeight="medium">{niceTypeTS(tile?.type)}</Text>
              {cohort && (
                <>
                  <Text fontSize="xs" color="gray.500" mt={2}>Cohort scope</Text>
                  <Text fontSize="sm" fontWeight="medium">{cohort.name}</Text>
                  {cohortSummary && <Text fontSize="xs" color="purple.700" fontFamily="mono">{cohortSummary}</Text>}
                </>
              )}
            </Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Raw config</Text>
            <Box as="pre" fontSize="xs" bg="gray.50" p={3} borderRadius="md" overflowX="auto">
              {JSON.stringify({
                type: tile?.type, source: tile?.source, cohort_scope: tile?.cohort_scope ? String(tile.cohort_scope) : null,
                refresh_frequency: tile?.refresh_frequency, viz_config: tile?.viz_config || {},
              }, null, 2)}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete confirm */}
      <AlertDialog isOpen={deleteAlert.isOpen} onClose={deleteAlert.onClose} leastDestructiveRef={cancelRef} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md">Delete tile?</AlertDialogHeader>
          <AlertDialogBody fontSize="sm">
            "{tile?.title}" will be removed from this board. This can't be undone.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={deleteAlert.onClose} size="sm">Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={busy} size="sm">Delete</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
